import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  useCreateBacklogItem,
  useUpdateBacklogItem,
  type ProjectBacklogItem,
  type ProjectEpic,
  type ProjectSprint,
  type BacklogItemType,
} from "@/hooks/useProjectPlanning";
import { SubtaskChecklist } from "./SubtaskChecklist";

const typeLabels: Record<BacklogItemType, string> = {
  task: "Tarefa",
  bug: "Bug",
  story: "História",
};
const priorityLabels = ["Baixa", "Normal", "Alta", "Urgente"];

const NONE = "none";

interface BacklogItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientProjectId: string;
  epics: ProjectEpic[];
  sprints: ProjectSprint[];
  item: ProjectBacklogItem | null;
  defaultSprintId?: string | null;
}

export function BacklogItemDialog({
  open,
  onOpenChange,
  clientProjectId,
  epics,
  sprints,
  item,
  defaultSprintId,
}: BacklogItemDialogProps) {
  const createItem = useCreateBacklogItem(clientProjectId);
  const updateItem = useUpdateBacklogItem(clientProjectId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [itemType, setItemType] = useState<BacklogItemType>("task");
  const [epicId, setEpicId] = useState(NONE);
  const [sprintId, setSprintId] = useState(NONE);
  const [storyPoints, setStoryPoints] = useState("");
  const [priority, setPriority] = useState(1);
  const [assignedTo, setAssignedTo] = useState("");
  const [dueDate, setDueDate] = useState("");

  useEffect(() => {
    if (!open) return;
    if (item) {
      setTitle(item.title);
      setDescription(item.description ?? "");
      setItemType(item.item_type);
      setEpicId(item.epic_id ?? NONE);
      setSprintId(item.sprint_id ?? NONE);
      setStoryPoints(item.story_points != null ? String(item.story_points) : "");
      setPriority(item.priority);
      setAssignedTo(item.assigned_to ?? "");
      setDueDate(item.due_date ?? "");
    } else {
      setTitle("");
      setDescription("");
      setItemType("task");
      setEpicId(NONE);
      setSprintId(defaultSprintId ?? NONE);
      setStoryPoints("");
      setPriority(1);
      setAssignedTo("");
      setDueDate("");
    }
  }, [open, item, defaultSprintId]);

  const handleSave = () => {
    if (!title.trim()) return;
    const newSprintId = sprintId === NONE ? null : sprintId;
    const currentStatus = item?.status ?? "backlog";
    // Moving a backlog-status item into a sprint promotes it onto the board (To Fazer).
    const status = newSprintId && currentStatus === "backlog" ? "todo" : currentStatus;

    const payload = {
      title: title.trim(),
      description: description.trim() || undefined,
      item_type: itemType,
      epic_id: epicId === NONE ? null : epicId,
      sprint_id: newSprintId,
      story_points: storyPoints ? Number(storyPoints) : null,
      priority,
      assigned_to: assignedTo.trim() || undefined,
      due_date: dueDate || undefined,
      status,
    };

    if (item) {
      updateItem.mutate({ id: item.id, data: payload }, { onSuccess: () => onOpenChange(false) });
    } else {
      createItem.mutate(payload, { onSuccess: () => onOpenChange(false) });
    }
  };

  const isPending = createItem.isPending || updateItem.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Editar item" : "Novo item"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="item-titulo">Título</Label>
            <Input
              id="item-titulo"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título do item"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="item-descricao">Descrição</Label>
            <Textarea
              id="item-descricao"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="item-tipo">Tipo</Label>
              <Select value={itemType} onValueChange={(v) => setItemType(v as BacklogItemType)}>
                <SelectTrigger id="item-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(typeLabels).map(([v, l]) => (
                    <SelectItem key={v} value={v}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="item-prioridade">Prioridade</Label>
              <Select value={String(priority)} onValueChange={(v) => setPriority(Number(v))}>
                <SelectTrigger id="item-prioridade">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorityLabels.map((l, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="item-epico">Épico</Label>
              <Select value={epicId} onValueChange={setEpicId}>
                <SelectTrigger id="item-epico">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Nenhum</SelectItem>
                  {epics.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="item-sprint">Sprint</Label>
              <Select value={sprintId} onValueChange={setSprintId}>
                <SelectTrigger id="item-sprint">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Backlog (sem sprint)</SelectItem>
                  {sprints.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="item-pontos">Pontos</Label>
              <Input
                id="item-pontos"
                type="number"
                min={0}
                value={storyPoints}
                onChange={(e) => setStoryPoints(e.target.value)}
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="item-responsavel">Responsável</Label>
              <Input
                id="item-responsavel"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                placeholder="Nome"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="item-prazo">Prazo</Label>
            <Input
              id="item-prazo"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          {item && (
            <div className="space-y-1.5 border-t pt-2">
              <span id="item-subtasks-label" className="text-sm font-medium leading-none">
                Subtarefas
              </span>
              <SubtaskChecklist backlogItemId={item.id} aria-labelledby="item-subtasks-label" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isPending || !title.trim()}>
            {isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
